<script setup>
import { computed, ref, watch } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import { MESSAGE_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';
import { useTranslationView } from 'dashboard/composables/useTranslationView';

const { content, attachments, contentAttributes, messageType } =
  useMessageContext();

const {
  hasTranslations,
  translationContent,
  isOutgoingTranslation,
  translationSkipped,
  sourceLanguage,
  targetLanguage,
} = useTranslations(contentAttributes);

const { showOriginals } = useTranslationView();

// Follows the thread-wide toggle, but a click on this message still wins until
// the thread-wide toggle moves again.
const renderOriginal = ref(showOriginals.value);
watch(showOriginals, value => {
  renderOriginal.value = value;
});

const renderContent = computed(() => {
  // Outgoing translated messages are stored the other way round: `content` is
  // what the customer received and the translation entry holds the agent's own
  // words. Defaulting to `content` means the bubble shows what was actually
  // sent, which is the whole point of being able to check.
  if (isOutgoingTranslation.value) {
    return renderOriginal.value ? translationContent.value : content.value;
  }

  if (renderOriginal.value) {
    return content.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return content.value;
});

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <BaseBubble class="px-4 py-3" data-bubble-name="text">
    <div class="gap-3 flex flex-col">
      <span v-if="isEmpty" class="text-n-slate-11">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>
      <FormattedContent v-if="renderContent" :content="renderContent" />
      <TranslationToggle
        v-if="hasTranslations"
        class="-mt-3"
        :showing-original="renderOriginal"
        :outgoing="isOutgoingTranslation"
        :source-language="sourceLanguage"
        :target-language="targetLanguage"
        @toggle="handleSeeOriginal"
      />
      <span
        v-if="translationSkipped && !hasTranslations"
        class="-mt-3 inline-flex items-center gap-1 text-xs text-n-slate-11"
      >
        <span class="i-lucide-languages size-3" />
        {{ $t('CONVERSATION.TRANSLATION.NOT_TRANSLATED') }}
      </span>
      <AttachmentChips :attachments="attachments" class="gap-2" />
      <template v-if="isTemplate">
        <div
          v-if="contentAttributes.submittedEmail"
          class="px-2 py-1 rounded-lg bg-n-alpha-3"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
